import React, { useState } from "react";
import {
  Box,
  Flex,
  Text,
} from "@chakra-ui/react";
import { FaList, FaChartBar, FaSearch } from "react-icons/fa";
import ProjectList from "./ProjectList";
import ProjectDetails from "./ProjectDetails";
import { Bar } from "react-chartjs-2";
import { Chart, registerables } from "chart.js";
import { IconWrapper } from "../../../components/IconWrapper";
import { useColorMode } from "@chakra-ui/react";
import { getThemeClasses } from "../../../utils/themeUtils";
import { getProjectScopeAccess } from "../../../utils/permissions";

Chart.register(...registerables);

export type Project = {
  id: string;
  name: string;
  description?: string;
  channels: string[];
  channelIds?: string[];
  assignedUsers?: string[];
  status?: string;
};
export type User = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  phone: string;
  assignedProjects?: string[];
  assignedTasks?: { id: string; name: string }[];
  organizationId: string;
  permissions?: "Admin" | "Editor" | "Viewer";
};

interface ProjectPerformanceProps {
  organizationId: string;
  projects: Project[];
  color: string;
  bg: string;
  users: User[];
  isAdmin : boolean;
  currentUser?: any;
}

const ProjectPerformance: React.FC<ProjectPerformanceProps> = ({
  organizationId,
  projects,
  color,
  bg,
  users,
  isAdmin,
  currentUser,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [filter, setFilter] = useState<string>("all");

  // Theme variables (match UserTable header/search styling)
  const { colorMode } = useColorMode();
  const isDark = colorMode === "dark";
  const theme = getThemeClasses(isDark);

  const filteredProjects = projects.filter((project) => {
    const matchesSearch = project.name
      .toLowerCase()
      .includes(searchTerm.toLowerCase());
    const matchesFilter =
      filter === "all" ||
      (filter === "active" && project.status === "active") ||
      (filter === "completed" && project.status === "completed") ||
      (filter === "assigned" &&
        project.assignedUsers &&
        project.assignedUsers.length > 0) ||
      (filter === "unassigned" &&
        (!project.assignedUsers || project.assignedUsers.length === 0));
    return matchesSearch && matchesFilter;
  });

  // Channel visibility is decided per project: an admin-level role — org-wide
  // or granted on that specific project — sees every channel, everyone else
  // stays limited to the channels assigned to them.
  const visibleProjects = filteredProjects.map((project) => {
    if (getProjectScopeAccess(currentUser, project.id).canViewAllChannels) {
      return project;
    }
    const assignedChannelIds = currentUser?.assignedChannels?.[project.id] || [];
    if (assignedChannelIds.length === 0) return project;
    const filteredChannels = project.channels.filter((_, idx) => {
      const chId = project.channelIds?.[idx];
      return chId ? assignedChannelIds.includes(chId) : true;
    });
    return { ...project, channels: filteredChannels };
  });

  const totalProjects = projects.length;
  const assignedProjects = projects.filter(
    (p) => p.assignedUsers && p.assignedUsers.length > 0
  ).length;
  const unassignedProjects = totalProjects - assignedProjects;
  const activeProjects = projects.filter((p) => p.status === "active").length;
  const completedProjects = projects.filter(
    (p) => p.status === "completed" || p.status === "Completed"
  ).length;

  return (
    <Flex direction="column"  width="100%">
      {!selectedProject && (
        <div className={`flex justify-between items-center p-4 ${theme.headerGradient} rounded-md mb-4 border ${theme.border} w-full`}>
          {isDark && (
            <>
              <span className={theme.headerOverlayGlow} />
              <span className={theme.headerOverlayTopLine} />
              <span className={theme.headerOverlayBottomGlow} />
              <span className={theme.headerOverlaySweep} />
            </>
          )}
          <div className="flex flex-col space-y-1">
            <span className={`text-2xl font-bold ${theme.text}`}>
              Project Performance Overview
            </span>
            <span className={`text-xs ${theme.textMuted}`}>
              View and analyze project performance data.
            </span>
          </div>
        </div>
      )}

      {!selectedProject && (
        <div className={`w-full rounded-md border ${theme.border} ${theme.card}`}>
          <div className={`flex justify-between items-center px-4 py-3 border-b ${theme.border} rounded-t-md flex-wrap`}>
            <div className={`relative flex items-center max-w-sm w-full rounded-full ${theme.cardMuted} overflow-hidden`}>
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <IconWrapper icon={FaSearch} className={`h-4 w-4 ${theme.textMuted}`} size={16} />
              </div>
              <input
                type="text"
                placeholder="Search projects..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`${theme.searchInput} block w-full pl-10 pr-3 py-1.5 border rounded-full leading-5 focus:outline-none sm:text-sm`}
              />
            </div>
          </div>
          <div className="p-4">
            <ProjectList
              color={color}
              bg={bg}
              projects={visibleProjects}
              onSelectProject={setSelectedProject}
            />
          </div>
        </div>
      )}

      <Box flex="1" width="100%" overflowY="auto">
        {selectedProject ? (
          <ProjectDetails
            color={color}
            bg={bg}
            project={selectedProject}
            onBack={() => setSelectedProject(null)}
            organizationId={organizationId}
            users={users}
            isAdmin={isAdmin}
            currentUser={currentUser}
          />
        ) : null}
      </Box>
    </Flex>
  );
};

export default ProjectPerformance;